import Axios from 'axios';
import axios from '../axios';

const enc = encodeURIComponent;
const base = '/assistant';

// The request body with the ids of attached files, when there are any.
function withFiles(body, attachments) {
  return attachments && attachments.length ? { ...body, attachments } : body;
}

export const getCapabilities = () => axios.get(`${base}/capabilities`);
export const getStatus = () => axios.get(`${base}/status`);
export const getComputers = () => axios.get(`${base}/computers`);

export const listConversations = (mode) => axios.get(`${base}/conversations`, { params: { mode } });
export const createConversation = (body) => axios.post(`${base}/conversations`, body);
export const getConversation = (id) => axios.get(`${base}/conversations/${enc(id)}`);
export const renameConversation = (id, title) => axios.patch(`${base}/conversations/${enc(id)}`, { title });
export const deleteConversation = (id) => axios.delete(`${base}/conversations/${enc(id)}`);
export const clearConversations = (mode) => axios.delete(`${base}/conversations`, { params: { mode } });
// attachments: ids of uploaded files (at most 5); the text may be empty when there are files.
export const sendMessage = (id, text, attachments) =>
  axios.post(`${base}/conversations/${enc(id)}/messages`, withFiles({ text }, attachments));
export const resumeConversation = (id) => axios.post(`${base}/conversations/${enc(id)}/resume`);
export const cancelConversation = (id) => axios.post(`${base}/conversations/${enc(id)}/cancel`);
export const getEvidence = (id, ids) =>
  axios.get(`${base}/conversations/${enc(id)}/evidence`, { params: { ids: ids.join(',') } });
export const listEvidence = (id, offset = 0, limit = 50) =>
  axios.get(`${base}/conversations/${enc(id)}/evidence`, { params: { offset, limit } });

// Files attached to the chat. The body is the file itself; the service answers { upload } once it has read it.
// onProgress(0..1) follows the transfer; cancelToken (uploadCanceller().token) stops it.
export const uploadFile = (file, onProgress, cancelToken) =>
  axios
    .post(`${base}/uploads`, file, {
      headers: { 'Content-Type': 'application/octet-stream', 'X-File-Name': encodeURIComponent(file.name) },
      onUploadProgress: (event) => {
        if (onProgress && event.total) onProgress(Math.min(event.loaded / event.total, 1));
      },
      cancelToken,
      timeout: 900000,
    })
    .then(({ data }) => data.upload);
// An uploaded file that is not attached to a sent message yet.
export const deleteUpload = (id) => axios.delete(`${base}/uploads/${enc(id)}`);
export const uploadCanceller = () => Axios.CancelToken.source();
export const isCancelled = (error) => Axios.isCancel(error);

export const getSettings = () => axios.get(`${base}/settings`);
export const saveSettings = (body) => axios.put(`${base}/settings`, body);
export const listModels = (body) => axios.post(`${base}/settings/models`, body);
export const testSettings = (body) => axios.post(`${base}/settings/test`, body);
export const loadModel = (body) => axios.post(`${base}/settings/load-model`, body);
// section: model | agent; fields (camelCase) — only these, all of the section when omitted.
export const resetSettings = (section, fields) => axios.post(`${base}/settings/reset`, { section, fields });

// Shared memory and organization instructions: everyone reads, administrators change.
// status: active | pending | rejected | archived (active and pending when omitted); q: text search.
export const listMemory = (params) => axios.get(`${base}/memory`, { params });
export const createMemory = (body) => axios.post(`${base}/memory`, body);
// body: text, subject, status (active | archived); a changed text is checked again.
export const updateMemory = (id, body) => axios.patch(`${base}/memory/${enc(id)}`, body);
export const deleteMemory = (id) => axios.delete(`${base}/memory/${enc(id)}`);
export const approveMemory = (id) => axios.post(`${base}/memory/${enc(id)}/approve`);
export const rejectMemory = (id) => axios.post(`${base}/memory/${enc(id)}/reject`);
// action: replace | keep_both | merge | discard; text: the merged text for merge.
export const resolveMemory = (id, action, text) =>
  axios.post(`${base}/memory/${enc(id)}/resolve`, text ? { action, text } : { action });
// All active entries against each other (model-bound: may take minutes).
export const checkMemory = () => axios.post(`${base}/memory/check`);
export const getInstructions = () => axios.get(`${base}/instructions`);
export const saveInstructions = (text) => axios.put(`${base}/instructions`, { text });

// Project registry and side work findings (administrators).
// params: q, status (active | closed).
export const listProjects = (params) => axios.get(`${base}/registry/projects`, { params });
export const createProject = (body) => axios.post(`${base}/registry/projects`, body);
export const updateProject = (id, body) => axios.patch(`${base}/registry/projects/${enc(id)}`, body);
export const deleteProject = (id) => axios.delete(`${base}/registry/projects/${enc(id)}`);
// body: filename, content (base64), apply, mode (merge | replace); apply: false only previews.
export const importRegistry = (body) => axios.post(`${base}/registry/import`, body);
// format: xlsx | csv -> { filename, contentType, content (base64), count }.
export const exportRegistry = (format) => axios.get(`${base}/registry/export`, { params: { format } });
export const getSideworkSettings = () => axios.get(`${base}/registry/settings`);
export const saveSideworkSettings = (settings) => axios.put(`${base}/registry/settings`, settings);
// params: status (new | seen | dismissed), day (YYYY-MM-DD).
export const listFindings = (params) => axios.get(`${base}/sidework/findings`, { params });
export const updateFinding = (id, status) => axios.patch(`${base}/sidework/findings/${enc(id)}`, { status });
export const getSideworkStatus = () => axios.get(`${base}/sidework/status`);
// day: YYYY-MM-DD (yesterday when omitted) -> { day, queued }; 409 while the user's own check runs or waits.
export const runSidework = (day) => axios.post(`${base}/sidework/run`, day ? { day } : {});
// The user's own daily check -> { enabled, time (HH:MM), lastAutoDay, paused (null | inactive | no-rights) }.
export const getSideworkSchedule = () => axios.get(`${base}/sidework/schedule`);
export const saveSideworkSchedule = (enabled, time) => axios.put(`${base}/sidework/schedule`, { enabled, time });

export function errorText(error, fallback = 'Не удалось выполнить запрос') {
  const data = error && error.response && error.response.data;
  if (data && typeof data.message === 'string' && data.message) return data.message;
  if (error && error.response) return `${fallback} (HTTP ${error.response.status})`;
  return `${fallback}: нет связи с сервером`;
}

/** Problem code of the web panel proxy: not-configured | unreachable | unauthorized ('' for other errors). */
export function errorCode(error) {
  const data = error && error.response && error.response.data;
  return data && typeof data.code === 'string' ? data.code : '';
}

const RETRY_DELAYS = [1000, 2000, 5000, 10000];

/**
 * Live events of a running turn (Server-Sent Events over GET).
 * The server first sends a snapshot of the run, then incremental events; "done" or "idle" ends the stream.
 * A broken stream is reopened and the fresh snapshot replaces the local live state.
 */
export function subscribeEvents(id, { onEvent, onError }) {
  let controller = null;
  let closed = false;
  let attempt = 0;
  let timer = null;

  const handleFrame = (frame) => {
    const data = frame
      .split('\n')
      .filter((line) => line.startsWith('data:'))
      .map((line) => line.slice(5).trimStart())
      .join('\n');
    if (!data) return null;
    const event = JSON.parse(data);
    onEvent(event);
    return event.type;
  };

  const connect = async () => {
    controller = new AbortController();
    let finished = false;
    let resync = false;
    try {
      const response = await fetch(`/api${base}/conversations/${enc(id)}/events`, {
        method: 'GET',
        credentials: 'same-origin',
        signal: controller.signal,
        headers: { Accept: 'text/event-stream' },
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        const error = new Error(body.message || `Поток событий недоступен (HTTP ${response.status})`);
        error.fatal = response.status < 500;
        throw error;
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      attempt = 0;
      for (;;) {
        // Frames must be read sequentially to keep UTF-8 and SSE framing intact.
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let boundary = buffer.indexOf('\n\n');
        while (boundary !== -1) {
          const type = handleFrame(buffer.slice(0, boundary));
          buffer = buffer.slice(boundary + 2);
          if (type === 'done' || type === 'idle') finished = true;
          if (type === 'resync') resync = true;
          boundary = buffer.indexOf('\n\n');
        }
      }
    } catch (error) {
      if (closed) return;
      if (error.fatal) {
        closed = true;
        if (onError) onError(error);
        return;
      }
    }
    if (closed || finished) return;
    const delay = resync ? 0 : RETRY_DELAYS[Math.min(attempt, RETRY_DELAYS.length - 1)];
    attempt += 1;
    timer = setTimeout(connect, delay);
  };

  connect();
  return {
    close() {
      closed = true;
      clearTimeout(timer);
      if (controller) controller.abort();
    },
  };
}
